import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { myFetch } from '@/commons/fetch';

// Types
interface ModelsState {
  models: any[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
}

const initialState: ModelsState = {
  models: [],
  isLoading: false,
  error: null,
  hasFetched: false,
};

// Async Thunks
export const fetchModels = createAsyncThunk(
  'models/fetchModels',
  async (username: string, { rejectWithValue }) => {
    try {
      const response = await myFetch(`/${username}/models`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch models');
    }
  }
);

export const createModel = createAsyncThunk(
  'models/createModel',
  async (
    { username, modelData }: { username: string; modelData: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await myFetch(`/${username}/models`, {
        method: 'POST',
        body: JSON.stringify(modelData),
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create model');
    }
  }
);

export const fetchModel = createAsyncThunk(
  'models/fetchModel',
  async (
    { username, modelId }: { username: string; modelId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await myFetch(`/${username}/models/${modelId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch model');
    }
  }
);

export const updateModel = createAsyncThunk(
  'models/updateModel',
  async (
    {
      username,
      modelId,
      modelData,
    }: { username: string; modelId: string; modelData: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await myFetch(`/${username}/models/${modelId}`, {
        method: 'PUT',
        body: JSON.stringify(modelData),
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update model');
    }
  }
);

// Slice
const modelsSlice = createSlice({
  name: 'models',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearModels: (state) => {
      state.models = [];
      state.error = null;
      state.hasFetched = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch Models
    builder
      .addCase(fetchModels.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchModels.fulfilled, (state, action) => {
        state.isLoading = false;
        state.models = action.payload;
        state.hasFetched = true;
      })
      .addCase(fetchModels.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.hasFetched = true;
      });

    // Create Model
    builder
      .addCase(createModel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createModel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.models = [...state.models, action.payload].sort(
          (a: any, b: any) =>
            new Date(b.updatedAt || b.createdAt).getTime() -
            new Date(a.updatedAt || a.createdAt).getTime()
        );
      })
      .addCase(createModel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Model (single)
    builder
      .addCase(fetchModel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchModel.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchModel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Model
    builder
      .addCase(updateModel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateModel.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the model in place without re-sorting
        // The component's own sorting logic will handle the display order
        const index = state.models.findIndex(
          (model) => model.id === action.payload.id
        );
        if (index !== -1) {
          state.models[index] = action.payload;
        }
      })
      .addCase(updateModel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearModels } = modelsSlice.actions;
export default modelsSlice.reducer;
